import { apiClient } from './api-client';

export const BUSINESS_TYPES = [
  { value: 'MANUFACTURER', label: 'Manufacturer' },
  { value: 'DISTRIBUTOR', label: 'Distributor' },
  { value: 'RESELLER', label: 'Reseller' },
  { value: 'DEALER', label: 'Dealer' },
  { value: 'SUPPLIER', label: 'Supplier' },
  { value: 'SERVICE_CENTER', label: 'Service Center' },
] as const;

export type BusinessTypeValue = (typeof BUSINESS_TYPES)[number]['value'];

export interface CompanyProfile {
  id: string;
  tenantId: string;
  registeredName: string | null;
  companyTitle: string | null;
  registrationNumber: string | null;
  taxNumber: string | null;
  phone: string | null;
  supportEmail: string | null;
  supportPhone: string | null;
  website: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  workingHours: string | null;
  supportHours: string | null;
  businessTypes: string[];
  createdAt: string;
  updatedAt: string;
}

export interface UpsertCompanyProfilePayload {
  registeredName?: string;
  companyTitle?: string;
  registrationNumber?: string;
  taxNumber?: string;
  phone?: string;
  supportEmail?: string;
  supportPhone?: string;
  website?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  logoUrl?: string;
  faviconUrl?: string;
  workingHours?: string;
  supportHours?: string;
  businessTypes?: string[];
}

/**
 * Shell-branding subset. Served by a separate endpoint that requires no
 * permission, so it resolves for every authenticated tenant user — unlike the
 * full profile, which is gated behind `company-profile:view`.
 */
export interface CompanyBranding {
  companyTitle: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
}

export const companyProfileService = {
  get: (): Promise<CompanyProfile> =>
    apiClient.get<CompanyProfile>('/company-profile').then((r) => r.data),

  getBranding: (): Promise<CompanyBranding> =>
    apiClient.get<CompanyBranding>('/company-profile/branding').then((r) => r.data),

  upsert: (payload: UpsertCompanyProfilePayload): Promise<CompanyProfile> =>
    apiClient.patch<CompanyProfile>('/company-profile', payload).then((r) => r.data),

  uploadLogo: (file: File): Promise<CompanyProfile> => {
    const form = new FormData();
    form.append('file', file);
    return apiClient
      .post<CompanyProfile>('/company-profile/upload/logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  uploadFavicon: (file: File): Promise<CompanyProfile> => {
    const form = new FormData();
    form.append('file', file);
    return apiClient
      .post<CompanyProfile>('/company-profile/upload/favicon', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
};
